import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stack,
    Typography,
    TextField,
    MenuItem,
    Radio,
    RadioGroup,
    FormControlLabel,
    Alert,
    Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAdjustInventory } from "../../hooks/inventory/useAdjustInventory";
import FormInput from "../inputs/FormInput";

// NOTE: Reason select stays as a raw MUI TextField select — FormInput's
// `select` type is wired to SelectAutoComplacent (FK-style options), not
// enum dropdowns like this one. The action Radio group has no equivalent
// in the inputs/ folder either.

const REASONS = [
    { value: "purchase",          labelEn: "Purchase",            labelAr: "شراء" },
    { value: "sale",              labelEn: "Sale",                labelAr: "بيع" },
    { value: "return",            labelEn: "Return",              labelAr: "مرتجع" },
    { value: "adjustment_add",    labelEn: "Adjustment (Add)",    labelAr: "تعديل (إضافة)" },
    { value: "adjustment_remove", labelEn: "Adjustment (Remove)", labelAr: "تعديل (إزالة)" },
    { value: "transfer_in",       labelEn: "Transfer In",         labelAr: "تحويل وارد" },
    { value: "transfer_out",      labelEn: "Transfer Out",        labelAr: "تحويل صادر" },
    { value: "damaged",           labelEn: "Damaged",             labelAr: "تالف" },
    { value: "correction",        labelEn: "Correction",          labelAr: "تصحيح" },
    { value: "initial",           labelEn: "Initial",             labelAr: "مخزون ابتدائي" },
];

const ACTION_DEFAULT_REASON = {
    add:    "adjustment_add",
    remove: "adjustment_remove",
    set:    "correction",
};

const AdjustStockModal = ({ open, stock, onClose }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");

    const [action, setAction] = useState("add");
    const [qty, setQty] = useState("");
    const [reason, setReason] = useState("adjustment_add");
    const [notes, setNotes] = useState("");

    const { adjustInventoryMutate, isAdjusting } = useAdjustInventory();

    useEffect(() => {
        if (!open) return;
        setAction("add");
        setQty("");
        setReason("adjustment_add");
        setNotes("");
    }, [open]);

    const product = stock?.product || {};
    const variant = stock?.variant || null;
    const warehouse = stock?.warehouse || {};
    const productName =
        product?.name?.[lng] || product?.name?.en || product?.name?.ar || product?.name || "-";
    const variantLabel =
        variant?.name?.[lng] || variant?.name?.en || variant?.name?.ar || variant?.name;

    const currentOnHand = stock?.on_hand ?? 0;
    const reserved = stock?.reserved ?? 0;
    const qtyNum = parseInt(qty, 10);

    let newOnHand = currentOnHand;
    if (Number.isFinite(qtyNum)) {
        if (action === "add") newOnHand = currentOnHand + qtyNum;
        else if (action === "remove") newOnHand = currentOnHand - qtyNum;
        else if (action === "set") newOnHand = qtyNum;
    }

    const wouldGoNegative =
        action !== "add" && Number.isFinite(qtyNum) && (newOnHand < 0 || newOnHand < reserved);

    const submitDisabled =
        !stock || !Number.isFinite(qtyNum) || qtyNum < 0 || wouldGoNegative || isAdjusting || !reason;

    const handleActionChange = (val) => {
        setAction(val);
        setReason(ACTION_DEFAULT_REASON[val] || reason);
    };

    const submit = () => {
        if (submitDisabled) return;
        adjustInventoryMutate(
            {
                warehouse_id: stock.warehouse_id ?? warehouse.id,
                product_id: stock.product_id ?? product.id,
                variant_id: stock.variant_id ?? variant?.id ?? null,
                action,
                qty: qtyNum,
                reason,
                notes: notes || undefined,
            },
            { onSuccess: () => onClose?.() }
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? "تعديل المخزون" : "Adjust Stock"}
            </DialogTitle>

            <DialogContent dividers>
                {/* Context */}
                <Box
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        bgcolor: (t) =>
                            t.palette.mode === "dark"
                                ? "rgba(255,255,255,0.04)"
                                : "rgba(0,0,0,0.03)",
                    }}
                >
                    <Typography variant="body2" fontWeight="bold">{productName}</Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 0.5 }}>
                        {variantLabel && (
                            <Chip size="small" label={variantLabel} color="primary" variant="tonal" />
                        )}
                        <Chip size="small" label={warehouse?.name || "-"} variant="outlined" />
                    </Stack>
                    <Stack direction="row" spacing={3} sx={{ mt: 1, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="text.secondary">
                            {isArabic ? "الحالي:" : "On hand:"} <strong>{currentOnHand}</strong>
                        </Typography>
                        <Typography variant="caption" color="warning.main">
                            {isArabic ? "محجوز:" : "Reserved:"} <strong>{reserved}</strong>
                        </Typography>
                    </Stack>
                </Box>

                <Stack spacing={2.5}>
                    {/* Action radio */}
                    <Box>
                        <Typography variant="subtitle2" fontWeight={700} mb={1}>
                            {isArabic ? "الإجراء" : "Action"}
                        </Typography>
                        <RadioGroup
                            row
                            value={action}
                            onChange={(e) => handleActionChange(e.target.value)}
                        >
                            <FormControlLabel value="add" control={<Radio />} label={isArabic ? "إضافة (+)" : "Add (+)"} />
                            <FormControlLabel value="remove" control={<Radio />} label={isArabic ? "إزالة (−)" : "Remove (−)"} />
                            <FormControlLabel value="set" control={<Radio />} label={isArabic ? "حجز (=)" : "Reserved (=)"} />
                        </RadioGroup>
                    </Box>

                    <FormInput
                        type="number"
                        name="qty"
                        label={isArabic ? "الكمية" : "Quantity"}
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                        required
                        placeholder=""
                    />

                    <TextField
                        select
                        label={isArabic ? "السبب" : "Reason"}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        required
                        fullWidth
                    >
                        {REASONS.map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                                {isArabic ? r.labelAr : r.labelEn}
                            </MenuItem>
                        ))}
                    </TextField>

                    <FormInput
                        type="textarea"
                        name="notes"
                        label={isArabic ? "ملاحظات (اختياري)" : "Notes (optional)"}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        required={false}
                        textareaRows="80px"
                        placeholder=""
                    />

                    {/* Preview */}
                    {Number.isFinite(qtyNum) && (
                        <Alert severity={wouldGoNegative ? "error" : "info"}>
                            {wouldGoNegative
                                ? (isArabic
                                    ? "العملية ستجعل المخزون سالباً أو أقل من المحجوز."
                                    : "This would push stock below zero / reserved.")
                                : (
                                    <>
                                        {isArabic ? "الكمية الجديدة:" : "New on hand:"}{" "}
                                        <strong>{newOnHand}</strong>
                                        {" "}
                                        <Typography component="span" variant="caption" color="text.secondary">
                                            ({currentOnHand} → {newOnHand})
                                        </Typography>
                                    </>
                                )
                            }
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isAdjusting}>
                    {isArabic ? "إلغاء" : "Cancel"}
                </Button>
                <Button
                    variant="contained"
                    onClick={submit}
                    disabled={submitDisabled}
                >
                    {isAdjusting
                        ? (isArabic ? "جاري..." : "Working...")
                        : (isArabic ? "تطبيق" : "Apply")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdjustStockModal;
